import React from 'react';
import { isNotEmpty } from '@intelligence/v3/utils/common';

/**
 * 可滚动的 overflow css 属性值
 */
const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'scroll', 'overlay'];

/**
 * 判断指定元素是否为真实的纵向滚动容器。
 *
 * 判定条件：
 * - `overflow-y` 为 `auto`、`scroll` 或 `overlay`
 * - `scrollHeight` 大于 `clientHeight`，即内容实际超出可视区域
 *
 * @param element 待判断的 DOM 元素
 * @returns 是否为真实可滚动的纵向滚动容器
 */
const isScrollableElement = (element?: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  const { overflowY } = window.getComputedStyle(element);

  if (!SCROLLABLE_OVERFLOW_VALUES.includes(overflowY)) {
    return false;
  }

  return element.scrollHeight > element.clientHeight;
};

/**
 * 判断当前页面根滚动容器是否存在实际滚动。
 *
 * 使用 `document.scrollingElement` 作为页面级滚动宿主，
 * 仅当内容高度超出可视区域时，才认为页面本身可滚动。
 *
 * @returns 页面是否存在真实的纵向滚动
 */
const isPageScrollable = (): boolean => {
  const { scrollingElement } = document;

  if (!scrollingElement) {
    return false;
  }

  return scrollingElement.scrollHeight > scrollingElement.clientHeight;
};

/**
 * 查找真实的滚动容器。
 *
 * 查找顺序：
 * 1. 从当前元素自身开始，沿父级链路逐级向上查找
 * 2. 命中首个真实可滚动的祖先元素时立即返回
 * 3. 如果祖先链路中未命中，但页面根滚动容器存在实际滚动，则返回 `window`
 * 4. 如果既没有元素级滚动容器，也没有页面级滚动，则返回 `null`
 *
 * 注意：
 * - 该方法只关注真实滚动能力，不处理 `affix-target` 等业务语义
 * - 返回 `window` 仅表示页面本身是当前滚动宿主
 * - 调用方需要处理返回 `null` 的情况
 *
 * @param element 当前节点或需要向上查找滚动宿主的起始节点
 * @returns 真实滚动容器、页面滚动宿主 `window`，或 `null`
 */
export const findScrollContainer = (element?: HTMLElement | null): HTMLElement | Window | null => {
  let currentElement = element;

  while (currentElement && currentElement !== document.body && currentElement !== document.documentElement) {
    if (isScrollableElement(currentElement)) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  if (isPageScrollable()) {
    return window;
  }

  return null;
};

/**
 * 获取元素滚动容器的 scrollTop 值（兼容 window）
 * @param target
 */
export const getScrollTop = (target?: HTMLElement | Window | null): number => {
  if (!target) {
    return Number.NaN;
  }

  if (target === window) {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  return (target as HTMLElement).scrollTop;
};

/**
 * 从 ReactNode 中递归提取可用于搜索、匹配或展示兜底的纯文本。
 *
 * 处理规则：
 * - `string` / `number`：直接转成字符串返回
 * - `ReactElement`：继续读取并解析 `props.children`
 * - `Array`：递归解析每一项，并用空格拼接
 * - `null` / `undefined` / `boolean` / 其他无法提取文本的节点：返回空字符串
 *
 * @param node 需要提取纯文本的 React 节点
 * @returns 从节点中提取出的纯文本，无法提取时返回空字符串
 */
export const getNodePlainText = (node?: React.ReactNode | null): string => {
  if (node === undefined || node === null || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node).trim();
  }

  if (Array.isArray(node)) {
    return node.map(getNodePlainText).filter(isNotEmpty).join(' ');
  }

  if (React.isValidElement(node)) {
    return getNodePlainText((node.props as any)?.children);
  }

  return '';
};
